// src/utils/notificationTarget.js
//
// Where a bell notification goes when it is clicked — one place, per role.
//
// Items used to carry a ready-made `to` string, built inline wherever the item
// was assembled in useNotifications.js. That spread the routing rules across
// every source, and the strings were frozen into the Read tab's snapshots, so a
// route that later moved (the navigator's AI-review queue left
// /dashboard#report-reviews for /escalations?tab=ai) kept sending people to the
// old place. Items now carry what they are about — `kind` and `ref` ids — and
// the route is worked out here at click time.
//
// Every target here is one the app already serves. Deep links to a single
// escalation / review / appointment are a separate change.
//
// ROLE RULES
// A target has to belong to the clicking account's portal: a DOCTOR only ever
// goes under /doctor/, a PATIENT under /patient/, and a navigator-portal
// account (HEALTH_NAVIGATOR, ADMIN, SUPPORT_STAFF) never under either.
// SUPPORT_STAFF only gets message notifications. Anything that falls outside
// those rules — or an unknown kind, or a missing id — resolves to null, and the
// bell says the item is no longer available instead of navigating somewhere
// wrong.

import { LAB_TEST_ORDER_PATH } from './labTestSuggestion';

const TEAM_THREAD_TYPES = ['NAVIGATOR_DOCTOR', 'SUBSCRIBER_NAVIGATOR'];
const SUPPORT_ROUTES = ['/patient-messages', '/team-chat'];

// Routes that moved, as saved in old Read-tab snapshots → where they live now.
// Navigator portal only: the doctor's own review queue never moved.
const MOVED_STAFF_ROUTES = {
  '/dashboard#report-reviews': '/escalations?tab=ai',
};

/** 'PATIENT' | 'DOCTOR' | 'SUPPORT' | 'STAFF' (the rest of the navigator portal). */
function portalOf(role) {
  if (role === 'PATIENT') return 'PATIENT';
  if (role === 'DOCTOR') return 'DOCTOR';
  if (role === 'SUPPORT_STAFF') return 'SUPPORT';
  return 'STAFF';
}

function pathname(path) {
  return path.split(/[?#]/)[0];
}

/** Whether `path` is somewhere the role's portal may send it. */
function allowedFor(portal, path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  if (portal === 'PATIENT') return path.startsWith('/patient/');
  if (portal === 'DOCTOR') return path.startsWith('/doctor/');
  if (path.startsWith('/patient/') || path.startsWith('/doctor/')) return false;
  if (portal === 'SUPPORT') return SUPPORT_ROUTES.includes(pathname(path));
  return true;
}

/**
 * Staff split conversations across two inboxes by type: patient-initiated
 * threads on the patient-messages inbox, NAVIGATOR_DOCTOR/SUBSCRIBER_NAVIGATOR
 * "team" threads on the team inbox — sending a team thread to the patient inbox
 * lands on a page that doesn't list it. A patient's threads all live on one page.
 */
function chatPath(portal, ref) {
  if (!ref.conversationId) return null;
  const query = `?conversationId=${encodeURIComponent(ref.conversationId)}`;
  if (portal === 'PATIENT') return `/patient/care-team${query}`;
  const team = TEAM_THREAD_TYPES.includes(ref.conversationType);
  if (portal === 'DOCTOR') return `${team ? '/doctor/team-chat' : '/doctor/patient-messages'}${query}`;
  return `${team ? '/team-chat' : '/patient-messages'}${query}`;
}

function patientPath(portal, patientId) {
  if (!patientId) return null;
  return portal === 'DOCTOR'
    ? `/doctor/patients/${encodeURIComponent(patientId)}`
    : `/patients/${encodeURIComponent(patientId)}`;
}

function pathFor(kind, portal, ref) {
  switch (kind) {
    case 'chat':
      return chatPath(portal, ref);
    case 'carePlan':
      if (!ref.itemId) return null;
      // A doctor's lab-test suggestion goes straight to the order flow.
      return ref.labTestSuggestion ? LAB_TEST_ORDER_PATH : '/patient/care-plan';
    case 'labDay':
      return ref.orderId ? `/patient/lab-tests/${encodeURIComponent(ref.orderId)}` : null;
    case 'labReport':
      if (!ref.recordId) return null;
      // The analysed result when there is one, otherwise the document list.
      return ref.triageResultId
        ? `/patient/results/${encodeURIComponent(ref.triageResultId)}`
        : '/patient/records';
    case 'plan':
      return '/patient/subscription';
    case 'review':
      if (!ref.triageResultId) return null;
      return portal === 'DOCTOR' ? '/doctor/home#report-reviews' : '/escalations?tab=ai';
    case 'escalation':
    case 'escalationAssigned':
      return patientPath(portal, ref.patientId);
    case 'followUp': {
      const base = patientPath(portal, ref.patientId);
      return base && `${base}?tab=Appointments`;
    }
    default:
      return null;
  }
}

/**
 * @param {{kind?:string, ref?:object, to?:string}} item  a live bell item, or a
 *        Read-tab snapshot. Snapshots saved before items had a `kind` carry only
 *        the `to` string they were built with; that is honoured only while it
 *        still belongs to the role's portal, after following a route that has
 *        since moved (MOVED_STAFF_ROUTES).
 * @param {string} role  the signed-in account's role — 'PATIENT', 'DOCTOR',
 *        'HEALTH_NAVIGATOR', 'ADMIN', 'SUPPORT_STAFF' (the bell's own
 *        'NAVIGATOR' is treated as the navigator portal).
 * @returns {{path:string} | null}
 */
export function notificationTarget(item, role) {
  if (!item) return null;
  const portal = portalOf(role);

  if (!item.kind) {
    const path = (portal === 'STAFF' && MOVED_STAFF_ROUTES[item.to]) || item.to;
    return allowedFor(portal, path) ? { path } : null;
  }
  if (portal === 'SUPPORT' && item.kind !== 'chat') return null;

  const path = pathFor(item.kind, portal, item.ref || {});
  return path && allowedFor(portal, path) ? { path } : null;
}
